import { useState, useEffect } from 'react';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Button from '../../components/button/Button';
import Icon from '../../components/icon/Icon';
import Table from '../../components/table/table';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import IntegrateContributionModal from '../../components/modals/IntegrateContributionModal';
import { getCapitalContributions } from '../../services/GuaranteesService';

interface CapitalContribution {
  id: number;
  contribution_date: string;
  expiration_date: string;
  currency_name: string;
  amount: number;
  integrated_amount: number;
  authorized_fund_name: string;
  is_active: boolean;
}

interface ProtectorDetailsContributionsSectionProps {
  protectorId?: string;
  onNewContribution?: () => void;
}

const ProtectorDetailsContributionsSection = ({ 
  protectorId,
  onNewContribution
}: ProtectorDetailsContributionsSectionProps) => {
  const [contributions, setContributions] = useState<CapitalContribution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 0, size: 10, totalElements: 0, totalPages: 0 });
  const [isIntegrateModalOpen, setIsIntegrateModalOpen] = useState(false);
  const [selectedContributionId, setSelectedContributionId] = useState<number | null>(null);

  useEffect(() => {
    if (protectorId) {
      fetchContributions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protectorId, pagination.page]);

  const fetchContributions = async () => {
    if (!protectorId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getCapitalContributions(protectorId, pagination.page, pagination.size);
      if (response.success && 'data' in response) {
        setContributions(response.data.content || []);
        setPagination({
          page: response.data.page || 0,
          size: response.data.size || 10,
          totalElements: response.data.totalElements || 0,
          totalPages: response.data.totalPages || 0
        });
      } else {
        setError('Error al cargar los aportes de capital');
      }
    } catch (err) {
      setError('Error al cargar los aportes de capital');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenIntegrateModal = (contributionId: number) => {
    setSelectedContributionId(contributionId);
    setIsIntegrateModalOpen(true);
  };

  const handleCloseIntegrateModal = () => {
    setIsIntegrateModalOpen(false);
    setSelectedContributionId(null);
  };

  const handleIntegrationSuccess = () => {
    fetchContributions();
  };

  const handleChangePage = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatAmount = (amount: number) => {
    if (amount === null || amount === undefined) {
      return '0';
    }
    if (Number.isInteger(amount)) {
      return amount.toLocaleString('es-AR');
    }
    return amount.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const columns = [
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Fecha de aporte</P>,
      selector: (row: CapitalContribution) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {formatDate(row.contribution_date)}
        </P>
      ),
      minWidth: '120px',
      grow: 1
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Fecha de vencimiento</P>,
      selector: (row: CapitalContribution) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {formatDate(row.expiration_date)}
        </P>
      ),
      minWidth: '140px',
      grow: 1
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Moneda</P>,
      selector: (row: CapitalContribution) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {row.currency_name || '-'}
        </P>
      ),
      minWidth: '100px',
      grow: 1
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Importe</P>,
      selector: (row: CapitalContribution) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {formatAmount(row.amount)}
        </P>
      ),
      minWidth: '120px',
      grow: 1
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Integrado</P>,
      selector: (row: CapitalContribution) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {formatAmount(row.integrated_amount || 0)}
        </P>
      ),
      minWidth: '120px',
      grow: 1
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Fondo autorizado</P>,
      selector: (row: CapitalContribution) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {row.authorized_fund_name || '-'}
        </P>
      ),
      minWidth: '150px',
      grow: 1.5
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Esta activo</P>,
      selector: (row: CapitalContribution) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {row.is_active ? 'Si' : 'No'}
        </P>
      ),
      minWidth: '100px',
      grow: 1
    },
    {
      selector: (row: CapitalContribution) => (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Icon 
            name='view_outline'
            variant='info'
            modifier='regular'
            width={24}
            height={24}
          />
          <Icon 
            name='plus_icon'
            variant='info'
            modifier='regular'
            width={24}
            height={24}
            onClick={() => handleOpenIntegrateModal(row.id)}
            cursor='pointer'
          />
        </div>
      ),
      minWidth: '120px',
      grow: 0.5
    }
  ];

  return (
    <>
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '3' }}>
        <H withMargin={false} type='2' weight='bold' modifier='strong'>
          Aportes de capital
        </H>
        {onNewContribution && (
          <Button variant='secondary' icon='plus_icon' iconAlign='left' onClick={onNewContribution}>
            Nuevo aporte
          </Button>
        )}
      </Card>
      
      {loading ? (
        <Card>
          <TableSkeleton rows={5} columns={7} />
        </Card>
      ) : error ? (
        <Card>
          <P variant='neutral' modifier='weak' withMargin={false}>
            {error}
          </P>
        </Card>
      ) : contributions && contributions.length > 0 ? (
        <Card>
          <Table
            columns={columns}
            data={contributions}
            tableData={pagination}
            handleChangePage={handleChangePage}
          />
        </Card>
      ) : (
        <Card>
          <P variant='neutral' modifier='weak' withMargin={false}>
            No hay aportes de capital para mostrar.
          </P>
        </Card>
      )}

      {selectedContributionId && (
        <IntegrateContributionModal
          isOpen={isIntegrateModalOpen}
          onClose={handleCloseIntegrateModal}
          contributionId={selectedContributionId}
          amount={contributions.find(c => c.id === selectedContributionId)?.amount || 0}
          integrated_amount={contributions.find(c => c.id === selectedContributionId)?.integrated_amount || 0}
          currency_name={contributions.find(c => c.id === selectedContributionId)?.currency_name || ''}
          onSuccess={handleIntegrationSuccess}
        />
      )}
    </>
  );
};

export default ProtectorDetailsContributionsSection;

